import { useState, useEffect, useRef } from "react"
import { useQueryState } from "nuqs"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown } from "@fortawesome/free-solid-svg-icons"
import Dropdown from "../../components/Dropdown"
import { useVideoLogs, useVideoLogById } from "../../hooks/useVideoLogs"
import ApiError from "../../components/ApiError"
import Loader from "../../components/Loader"
import { getDateRange } from "../../utils/datetime"

const parseMatchingVideos = (matchingVideos) => {
  if (!matchingVideos) return []
  if (Array.isArray(matchingVideos)) return matchingVideos
  try {
    const parsed = JSON.parse(matchingVideos)
    return parsed
  } catch {
    return []
  }
}

const EducationalVideoLogs = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const [activeSearchTerm, setActiveSearchTerm] = useQueryState("search", {
    defaultValue: "",
  })
  const [dateRange, setDateRange] = useQueryState("dateRange", {
    defaultValue: "Today",
  })
  const [status, setStatus] = useQueryState("status", {
    defaultValue: "All",
  })
  const [expandedRow, setExpandedRow] = useState(null)
  const [selectedLogId, setSelectedLogId] = useState(null)

  const sentinelRef = useRef(null)
  const expandedRowRef = useRef(null)

  const toggleRowExpansion = (rowId) => {
    setExpandedRow((prev) => (prev === rowId ? null : rowId))
  }

  useEffect(() => {
    if (expandedRow && expandedRowRef.current) {
      const scrollToRow = () => {
        expandedRowRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        })
      }
      setTimeout(scrollToRow, 100)
      setTimeout(scrollToRow, 300)
    }
  }, [expandedRow])

  const getDateFilterValue = (range) => {
    if (range === "All") return undefined
    const interval = getDateRange(range)
    return [interval.start.toISODate(), interval.end.toISODate()].join(",")
  }

  const {
    data: { data, total } = { data: [], total: 0 },
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useVideoLogs({
    limit: 20,
    sortBy: "CreatedDate:DESC",
    filters: activeSearchTerm?.trim()
      ? { searchTerm: activeSearchTerm.trim() }
      : {
          timePeriod:
            dateRange === "All" ? undefined : getDateFilterValue(dateRange),
          status: status === "All" ? undefined : status.toLowerCase(),
        },
  })

  const { data: logDetail, isLoading: isDetailLoading } =
    useVideoLogById(selectedLogId)

  useEffect(() => {
    setExpandedRow(null)
  }, [activeSearchTerm, dateRange, status])

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { rootMargin: "200px", threshold: 0.1 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const dateRangeOptions = [
    "Today",
    "Yesterday",
    "This Week",
    "Last Week",
    "This Month",
    "Last Month",
    "All",
  ]
  const statusOptions = ["All", "Success", "Errored"]

  const handleClearAll = () => {
    setDateRange("Today")
    setStatus("All")
  }

  const handleClearSearch = () => {
    setSearchTerm("")
    setActiveSearchTerm("")
  }

  if (isError) {
    return (
      <div className="mt-4">
        <ApiError axiosError={error} />
      </div>
    )
  }

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-2">Report Videos Logs</h1>

      {/* Filter Row */}
      <div className="bg-gray-50 p-4 rounded-lg mb-2">
        <div className="flex gap-4">
          {/* Date Range */}
          <div>
            <Dropdown
              disabled={!!activeSearchTerm}
              label="DATE RANGE"
              options={activeSearchTerm ? [""] : dateRangeOptions}
              setValue={setDateRange}
              title={
                activeSearchTerm
                  ? "Clear the search field to enable filters"
                  : "Filter by date range"
              }
              value={activeSearchTerm ? "" : dateRange}
            />
          </div>

          {/* Status */}
          <div>
            <Dropdown
              disabled={!!activeSearchTerm}
              label="STATUS"
              options={activeSearchTerm ? [""] : statusOptions}
              setValue={setStatus}
              title={
                activeSearchTerm
                  ? "Clear the search field to enable filters"
                  : "Filter by success/error status"
              }
              value={activeSearchTerm ? "" : status}
            />
          </div>

          <div className="flex items-end gap-2">
            <button
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
              disabled={!!activeSearchTerm}
              title={
                activeSearchTerm
                  ? "Clear the search field to enable filters"
                  : "Clear all filters"
              }
              onClick={handleClearAll}
            >
              Clear
            </button>
          </div>

          <div className="flex-1" />

          {/* Accession Number */}
          <div className="flex items-end gap-2">
            <div>
              <label className="block text-xs font-medium mb-1 ml-1 text-gray-700">
                ACCESSION NUMBER
              </label>
              <input
                className="w-full min-w-[300px] px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g. AN-2026-00142"
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    setActiveSearchTerm(searchTerm)
                  }
                }}
              />
            </div>
            <button
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium disabled:cursor-not-allowed disabled:bg-blue-400"
              onClick={() => setActiveSearchTerm(searchTerm)}
            >
              Search
            </button>
            <button
              className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
              onClick={handleClearSearch}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="mt-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-20 gap-4">
            <Loader />
            <span className="text-gray-600">Loading video logs...</span>
          </div>
        ) : (
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs text-gray-500">
                Showing <span className="font-bold">{data.length}</span> of{" "}
                <span className="font-bold">{total}</span> records
              </span>
            </div>

            {data.length > 0 ? (
              <div className="space-y-2 max-h-[calc(100vh-325px)] overflow-auto">
                {data.map((log) => {
                  const videos = parseMatchingVideos(log.MatchingVideos)
                  const isExpanded = expandedRow === log.ReportVideosLogId

                  return (
                    <div
                      key={log.ReportVideosLogId}
                      ref={isExpanded ? expandedRowRef : null}
                      className={`bg-white border rounded-lg hover:shadow-sm transition-shadow overflow-hidden ${
                        isExpanded
                          ? "border-gray-400 shadow-sm border-2"
                          : "border-gray-200"
                      }`}
                    >
                      {/* Main Row */}
                      <div
                        className={`flex items-center justify-between p-2.5 hover:cursor-pointer rounded-lg hover:bg-gray-100 ${isExpanded ? "bg-gray-200" : ""}`}
                        onClick={() => {
                          toggleRowExpansion(log.ReportVideosLogId)
                          setSelectedLogId(log.ReportVideosLogId)
                        }}
                      >
                        <div className="flex items-center gap-4">
                          {/* Status dot */}
                          <div
                            className={`w-2 h-2 rounded-full flex-shrink-0 ${
                              log.IsError ? "bg-red-500" : "bg-green-500"
                            }`}
                          />

                          {/* Accession Number */}
                          <span className="font-semibold text-gray-900">
                            {log.AccessionNo}
                          </span>

                          {/* Videos matched */}
                          <span className="px-2 py-0.5 text-xs rounded-full flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-700">
                            {videos.length} video
                            {videos.length !== 1 ? "s" : ""}
                          </span>
                        </div>

                        <div className="flex items-center gap-2 text-gray-500">
                          <span className="text-sm">
                            {new Date(log.CreatedDate).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              }
                            )}
                          </span>
                          <FontAwesomeIcon
                            className={`w-4 h-4 transition-transform ${
                              isExpanded ? "rotate-180" : ""
                            }`}
                            icon={faChevronDown}
                          />
                        </div>
                      </div>

                      {/* Accordion Content */}
                      {isExpanded &&
                        (isDetailLoading ? (
                          <div className="border-t border-gray-200 p-4 bg-gray-50">
                            <div className="flex flex-col gap-4">
                              {/* Row 1: details card + physicians table */}
                              <div className="flex gap-4">
                                {/* Details card skeleton */}
                                <div className="rounded-md bg-gray-100 border border-gray-300 flex-1">
                                  <div className="px-3 py-2 bg-gray-200 border-b border-gray-300 flex gap-2">
                                    <div className="w-16 h-4 bg-gray-300 rounded-full animate-pulse" />
                                    <div className="w-12 h-4 bg-gray-300 rounded-full animate-pulse" />
                                  </div>
                                  <div className="p-3 space-y-3">
                                    <div>
                                      <div className="w-20 h-3 bg-gray-300 rounded animate-pulse mb-2" />
                                      <div className="flex flex-wrap gap-1">
                                        {[1, 2, 3].map((i) => (
                                          <div
                                            key={i}
                                            className="w-16 h-5 bg-indigo-100 border border-indigo-200 rounded-full animate-pulse"
                                          />
                                        ))}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="w-20 h-3 bg-gray-300 rounded animate-pulse mb-2" />
                                      <div className="flex flex-wrap gap-1">
                                        {[1, 2].map((i) => (
                                          <div
                                            key={i}
                                            className="w-20 h-5 bg-amber-100 border border-amber-200 rounded-full animate-pulse"
                                          />
                                        ))}
                                      </div>
                                    </div>
                                    <div>
                                      <div className="w-20 h-3 bg-gray-300 rounded animate-pulse mb-2" />
                                      <div className="flex flex-wrap gap-1">
                                        {[1, 2].map((i) => (
                                          <div
                                            key={i}
                                            className="w-20 h-5 bg-green-100 border border-green-200 rounded-full animate-pulse"
                                          />
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                {/* Physicians table skeleton */}
                                <div className="rounded-md bg-gray-100 border border-gray-300 flex-1 overflow-hidden">
                                  <div className="px-3 py-2 bg-gray-200 border-b border-gray-300">
                                    <div className="w-32 h-3 bg-gray-300 rounded animate-pulse" />
                                  </div>
                                  <div className="divide-y divide-gray-200">
                                    {[1, 2, 3].map((i) => (
                                      <div
                                        key={i}
                                        className="flex gap-4 px-3 py-2"
                                      >
                                        <div className="w-28 h-3 bg-gray-200 rounded animate-pulse" />
                                        <div className="w-20 h-3 bg-gray-200 rounded animate-pulse" />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="border-t border-gray-200 p-4 bg-gray-50">
                            {/* Detail Cards */}
                            {logDetail ? (
                              <div className="flex flex-col gap-4">
                                {log?.IsError && (
                                  <div className="bg-red-100 text-red-800 rounded-md p-3 text-sm">
                                    Error:{" "}
                                    {log?.ErrorDescription ||
                                      "An error occurred while processing this video log."}
                                  </div>
                                )}
                                <div className="flex gap-4">
                                  <div
                                    key={logDetail.ReportVideosLogDetailId}
                                    className="rounded-md bg-white border border-gray-300 flex-1"
                                  >
                                    <div className="flex justify-between px-3 py-2 bg-gray-200 border-b border-gray-300">
                                      <div className="flex gap-2">
                                        <span className="text-sm font-medium text-gray-700">
                                          {logDetail?.PatientAge}y -{" "}
                                          {logDetail?.PatientGender}
                                        </span>
                                        {logDetail.BodyPart && (
                                          <span className="px-2 py-0.5 text-xs rounded-full bg-blue-200 border border-blue-300 text-blue-700 font-medium">
                                            {logDetail.BodyPart?.toUpperCase() ||
                                              "N/A"}
                                          </span>
                                        )}
                                        {/* Radiologist */}
                                        {log.radiologistProfile && (
                                          <span className="text-sm text-gray-500">
                                            Reading Radiologist:{" "}
                                            {log.radiologistProfile.FirstName}{" "}
                                            {log.radiologistProfile.LastName}
                                            {log.radiologistProfile.Credentials
                                              ? `, ${log.radiologistProfile.Credentials}`
                                              : ""}
                                          </span>
                                        )}
                                      </div>

                                      {log.ReportVideosUuid &&
                                        videos.length > 0 && (
                                          <div>
                                            <a
                                              className="px-2 py-1 text-xs rounded bg-primary text-white hover:bg-primary/80 transition-colors font-medium"
                                              href={`${window.location.origin}/educational-videos/${log.ReportVideosUuid}`}
                                              rel="noopener noreferrer"
                                              target="_blank"
                                            >
                                              Preview
                                            </a>
                                          </div>
                                        )}
                                    </div>

                                    <div className="flex flex-col gap-4 p-3">
                                      <div>
                                        <span className="text-sm font-semibold text-gray-700 uppercase">
                                          Pathologies
                                        </span>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                          {logDetail.Pathologies ? (
                                            logDetail.Pathologies.split(",")
                                              .map((p) => p.trim())
                                              .filter(Boolean)
                                              .map((p, i) => (
                                                <span
                                                  key={i}
                                                  className="px-2 py-0.5 text-xs rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700"
                                                >
                                                  {p}
                                                </span>
                                              ))
                                          ) : (
                                            <span className="text-xs text-gray-400 italic">
                                              N/A
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <div>
                                        <span className="text-sm font-semibold text-gray-700 uppercase">
                                          Indications
                                        </span>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                          {logDetail.Indications ? (
                                            logDetail.Indications.split(",")
                                              .map((p) => p.trim())
                                              .filter(Boolean)
                                              .map((p, i) => (
                                                <span
                                                  key={i}
                                                  className="px-2 py-0.5 text-xs rounded-full bg-amber-50 border border-amber-400 text-amber-800"
                                                >
                                                  {p}
                                                </span>
                                              ))
                                          ) : (
                                            <span className="text-xs text-gray-400 italic">
                                              N/A
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <div>
                                        <span className="text-sm font-semibold text-gray-700 uppercase">
                                          Referring Physicians Matched
                                        </span>
                                        <div className="flex flex-col mt-1">
                                          {logDetail.MatchingPhysicians ? (
                                            (() => {
                                              const physicians =
                                                logDetail.MatchingPhysicians.split(
                                                  "|"
                                                )
                                                  .map((p) => p.trim())
                                                  .filter(Boolean)
                                              const hasMultiple =
                                                physicians.length > 1
                                              return (
                                                <>
                                                  {physicians.map((p, i) => {
                                                    const commaIdx =
                                                      p.indexOf(",")
                                                    const npi =
                                                      commaIdx !== -1
                                                        ? p
                                                            .slice(0, commaIdx)
                                                            .trim()
                                                        : p.trim()
                                                    const name =
                                                      commaIdx !== -1
                                                        ? p
                                                            .slice(commaIdx + 1)
                                                            .trim()
                                                        : ""
                                                    const isSelected =
                                                      hasMultiple &&
                                                      npi ===
                                                        logDetail.SelectedPhysicianNpi &&
                                                      name ===
                                                        logDetail.SelectedPhysicianName
                                                    return (
                                                      <div
                                                        key={i}
                                                        className={`flex items-center flex-wrap gap-1 text-xs py-1 border-b border-gray-100 last:border-0 ${
                                                          isSelected
                                                            ? "text-green-700 font-medium"
                                                            : "text-gray-700"
                                                        }`}
                                                      >
                                                        <span className="shrink-0">
                                                          {name
                                                            ? `${name} - ${npi}`
                                                            : npi}
                                                        </span>
                                                        {(isSelected ||
                                                          !hasMultiple) &&
                                                          logDetail.SelectedPhysicianSpecialties &&
                                                          logDetail.SelectedPhysicianSpecialties.split(
                                                            ","
                                                          )
                                                            .map((s) =>
                                                              s.trim()
                                                            )
                                                            .filter(Boolean)
                                                            .map((s, si) => (
                                                              <span
                                                                key={si}
                                                                className="px-2 py-0.5 text-[10px] rounded-full bg-green-50 border border-green-200 text-green-700 font-normal"
                                                              >
                                                                {s}
                                                              </span>
                                                            ))}
                                                      </div>
                                                    )
                                                  })}
                                                </>
                                              )
                                            })()
                                          ) : (
                                            <span className="text-xs text-gray-400 italic">
                                              N/A
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  {(() => {
                                    const matchedVideos = parseMatchingVideos(
                                      log.MatchingVideos
                                    )
                                    return (
                                      <div className="rounded-md bg-white border border-gray-300 flex-1 overflow-hidden">
                                        <div className="px-3 py-2 bg-gray-200 border-b border-gray-300">
                                          <span className="text-xs font-semibold text-gray-700 uppercase">
                                            Report Videos
                                          </span>
                                        </div>
                                        {matchedVideos.length > 0 ? (
                                          <div className="flex flex-col gap-1 p-2">
                                            {matchedVideos.map((video, i) => (
                                              <div
                                                key={i}
                                                className="flex items-start gap-1 px-2 py-1 rounded bg-white "
                                                title={video?.VideoTitle}
                                              >
                                                <span className="text-xs font-bold text-gray-400 mt-0.5 flex-shrink-0">
                                                  {i + 1}.
                                                </span>
                                                <span className="text-base text-gray-700 leading-snug line-clamp-2">
                                                  {video?.VideoTitle ||
                                                    JSON.stringify(video)}
                                                </span>
                                              </div>
                                            ))}
                                          </div>
                                        ) : (
                                          <p className="px-3 py-3 text-xs text-gray-400 italic">
                                            No report videos
                                          </p>
                                        )}
                                      </div>
                                    )
                                  })()}
                                </div>
                              </div>
                            ) : (
                              <div className="text-sm text-gray-500 italic mb-3">
                                No further detail available
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  )
                })}

                {/* Infinite Scroll Sentinel */}
                <div ref={sentinelRef} className="h-1" />

                {/* Fallback load-more */}
                {hasNextPage && (
                  <div className="flex justify-center py-4">
                    {isFetchingNextPage ? (
                      <div className="flex items-center gap-2">
                        <Loader />
                        <span className="text-gray-600">
                          Loading more records...
                        </span>
                      </div>
                    ) : (
                      <button
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition-colors"
                        disabled={isFetchingNextPage}
                        onClick={fetchNextPage}
                      >
                        Load More
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No records found. Try adjusting your filters.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default EducationalVideoLogs
